/**
 * Live updates (BUILD-PLAN 2.2): one SSE connection shared by every Velora
 * window (QA r57, see shared.ts; one per tab where the browser cannot share),
 * scoped to the office selector. `entity.changed` events invalidate the matching TanStack
 * queries (batched), so screens refetch through the normal RLS-protected
 * endpoints; events carry ids only, never PHI.
 *
 *   useLive('appointment')                    // refetch on change (by key convention)
 *   useLive('appointment', (e) => flash(e.id)) // plus a custom reaction
 *   useLiveDelta('appointment', (e) => ...)   // patch instead of refetch (below)
 *
 * Delta: a query with `meta: { liveDelta: '<entityKey>' }` is not refetched
 * by that entity's events while a `useLiveDelta` handler for the key is
 * mounted and the query is on screen: the handler patches it (for example
 * the schedule reads only the changed appointments). Off screen it is only
 * marked stale, so it refreshes in the background when it is shown again.
 */
import { useQueryClient } from '@tanstack/react-query';
import type { EntityChanged } from '@velora/contracts';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useOfficeScope } from '../office/OfficeScope.js';
import { queryKeyMatchesEntity, type LiveStatus } from './client.js';
import { createLiveConnection } from './shared.js';

type Handler = (event: EntityChanged) => void;

interface LiveContextValue {
  status: LiveStatus;
  /** Last time any event or (re)connect arrived. */
  lastEventAt: number | null;
  subscribe: (entityKey: string, handler: Handler) => () => void;
  /** Marks `entityKey`'s delta queries as patched by a handler; returns the release. */
  claimDelta: (entityKey: string) => () => void;
}

const LiveContext = createContext<LiveContextValue>({
  status: 'idle',
  lastEventAt: null,
  subscribe: () => () => undefined,
  claimDelta: () => () => undefined,
});

/** True when a mounted delta handler patches this on-screen query instead of a refetch. */
export function patchedByDelta(
  query: { meta?: Record<string, unknown> | undefined; getObserversCount(): number },
  entityKey: string,
  claimed: ReadonlyMap<string, number>,
): boolean {
  return (
    (claimed.get(entityKey) ?? 0) > 0 &&
    query.meta?.['liveDelta'] === entityKey &&
    query.getObserversCount() > 0
  );
}

const FLUSH_MS = 150;

export function LiveProvider({ enabled, children }: { enabled: boolean; children: ReactNode }) {
  const queryClient = useQueryClient();
  const { param, locationIds } = useOfficeScope();
  const [status, setStatus] = useState<LiveStatus>('idle');
  const [lastEventAt, setLastEventAt] = useState<number | null>(null);
  const handlers = useRef(new Map<string, Set<Handler>>());
  const pending = useRef(new Set<string>());
  const claimed = useRef(new Map<string, number>());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const client = useMemo(() => {
    if (!enabled || typeof window === 'undefined' || typeof EventSource === 'undefined') {
      return null;
    }
    const flush = () => {
      timer.current = null;
      const keys = [...pending.current];
      pending.current.clear();
      void queryClient.invalidateQueries({
        predicate: (q) =>
          keys.some(
            (k) => queryKeyMatchesEntity(q.queryKey, k) && !patchedByDelta(q, k, claimed.current),
          ),
      });
    };
    return createLiveConnection({
      onStatus: setStatus,
      onChange: (event) => {
        setLastEventAt(Date.now());
        for (const h of handlers.current.get(event.entityKey) ?? []) h(event);
        pending.current.add(event.entityKey);
        timer.current ??= setTimeout(flush, FLUSH_MS);
      },
      onResync: () => {
        setLastEventAt(Date.now());
        // We may have missed events: refetch everything that is live-backed.
        const keys = [...handlers.current.keys()];
        void queryClient.invalidateQueries({
          predicate: (q) =>
            keys.some((k) => queryKeyMatchesEntity(q.queryKey, k)) || q.queryKey[0] === 'shell',
        });
      },
    });
  }, [enabled, queryClient]);

  useEffect(() => {
    if (!client || locationIds.length === 0) return undefined;
    client.connect(param);
    return undefined;
  }, [client, param, locationIds.length]);

  useEffect(
    () => () => {
      client?.close();
      if (timer.current) clearTimeout(timer.current);
    },
    [client],
  );

  const subscribe = useCallback((entityKey: string, handler: Handler) => {
    const set = handlers.current.get(entityKey) ?? new Set<Handler>();
    set.add(handler);
    handlers.current.set(entityKey, set);
    return () => {
      set.delete(handler);
      if (set.size === 0) handlers.current.delete(entityKey);
    };
  }, []);

  const claimDelta = useCallback((entityKey: string) => {
    claimed.current.set(entityKey, (claimed.current.get(entityKey) ?? 0) + 1);
    let released = false;
    return () => {
      if (released) return;
      released = true;
      const n = (claimed.current.get(entityKey) ?? 1) - 1;
      if (n > 0) claimed.current.set(entityKey, n);
      else claimed.current.delete(entityKey);
    };
  }, []);

  const value = useMemo<LiveContextValue>(
    () => ({ status, lastEventAt, subscribe, claimDelta }),
    [status, lastEventAt, subscribe, claimDelta],
  );

  return <LiveContext.Provider value={value}>{children}</LiveContext.Provider>;
}

/**
 * Subscribes the calling screen to an entity's live changes. Queries whose
 * key starts with `entityKey` refetch automatically; `onChange` runs for
 * each event (for flashes like "moved by Kim"). Returns the stream status.
 */
export function useLive(entityKey: string, onChange?: Handler): LiveStatus {
  const ctx = useContext(LiveContext);
  const ref = useRef(onChange);
  ref.current = onChange;
  const { subscribe } = ctx;
  useEffect(() => subscribe(entityKey, (e) => ref.current?.(e)), [subscribe, entityKey]);
  return ctx.status;
}

/**
 * Like {@link useLive}, but on-screen queries tagged `meta.liveDelta ===
 * entityKey` are left to `onChange` to patch (no refetch). `onChange` sees
 * every event of the key; queries without the tag still refetch as usual,
 * and a resync (missed events) still refetches everything.
 */
export function useLiveDelta(entityKey: string, onChange: Handler): LiveStatus {
  const ctx = useContext(LiveContext);
  const ref = useRef(onChange);
  ref.current = onChange;
  const { subscribe, claimDelta } = ctx;
  useEffect(() => {
    const release = claimDelta(entityKey);
    const off = subscribe(entityKey, (e) => ref.current(e));
    return () => {
      off();
      release();
    };
  }, [subscribe, claimDelta, entityKey]);
  return ctx.status;
}

export function useLiveStatus(): { status: LiveStatus; lastEventAt: number | null } {
  const { status, lastEventAt } = useContext(LiveContext);
  return { status, lastEventAt };
}
